import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import LanguageRouter from "./components/LanguageRouter";
import Analytics from "./components/Analytics";
import ShortRedirect from "./components/ShortRedirect";
import SplashScreen from "./components/SplashScreen";

// Resilient lazy loader: a route chunk request can occasionally hang or fail
// (flaky mobile networks, a stale chunk after a redeploy). A plain lazy() would
// leave the user stuck on the Suspense spinner forever. lazyWithRetry races the
// import against a timeout, retries a few times, and as a last resort forces a
// one-time full reload so a fresh chunk graph is fetched.
const lazyWithRetry = (factory, retries = 3, timeoutMs = 9000) =>
  lazy(() => {
    const attempt = (left) =>
      Promise.race([
        factory(),
        new Promise((_, reject) => setTimeout(() => reject(new Error("chunk-timeout")), timeoutMs)),
      ]).catch((err) => {
        if (left > 0) return new Promise((res) => setTimeout(res, 400)).then(() => attempt(left - 1));
        const key = "chunk_reloaded";
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, "1");
          window.location.reload();
          return new Promise(() => {}); // hold until reload
        }
        throw err;
      });
    return attempt(retries).then((mod) => {
      sessionStorage.removeItem("chunk_reloaded");
      return mod;
    });
  });

// Route-level code splitting: each page ships as its own chunk, so the
// landing page no longer downloads the heavy investor pitch (recharts),
// the maps (leaflet) or the PayPal SDK until they are actually needed.
const CommercialHome = lazyWithRetry(() => import("./components/CommercialHome"));
const PitchDeck = lazyWithRetry(() => import("./components/PitchDeck"));
const PitchDeckEN = lazyWithRetry(() => import("./components/PitchDeckEN"));
const SupportPage = lazyWithRetry(() => import("./components/SupportPage"));
const ExperiencesPage = lazyWithRetry(() => import("./components/ExperiencesPage"));
const SharedCruisesPage = lazyWithRetry(() => import("./components/SharedCruisesPage"));
const AdminPage = lazyWithRetry(() => import("./components/AdminPage"));
const BrandKit = lazyWithRetry(() => import("./components/BrandKit"));
const SnapshotEN = lazyWithRetry(() => import("./components/SnapshotEN"));
const UtmLinks = lazyWithRetry(() => import("./components/UtmLinks"));
const Legal = lazyWithRetry(() => import("./components/Legal"));
const BalancePage = lazyWithRetry(() => import("./components/BalancePage"));
const CuratedBooking = lazyWithRetry(() => import("./components/CuratedBooking"));

const Loader = () => (
  <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#F7F4EF" }}>
    <div style={{ width: 34, height: 34, border: "3px solid #C9A84C", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
  </div>
);

// Router setup with IT + EN routes + auto language detection at /
function App() {
  return (
    <div className="App">
      <SplashScreen />
      <BrowserRouter>
        <Analytics />
        <Suspense fallback={<Loader />}>
          <Routes>
            <Route path="/" element={<LanguageRouter />} />
            <Route path="/it" element={<CommercialHome lang="it" />} />
            <Route path="/en" element={<CommercialHome lang="en" />} />
            <Route path="/pitch" element={<PitchDeck />} />
            <Route path="/pitch-en" element={<PitchDeckEN />} />
            <Route path="/investitori" element={<Navigate to="/pitch" replace />} />
            <Route path="/investors" element={<Navigate to="/pitch-en" replace />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/sostieni" element={<Navigate to="/support" replace />} />
            <Route path="/experiences" element={<ExperiencesPage />} />
            <Route path="/esperienze" element={<Navigate to="/experiences" replace />} />
            <Route path="/crociere" element={<SharedCruisesPage />} />
            <Route path="/cruises" element={<SharedCruisesPage />} />
            <Route path="/brand-kit" element={<BrandKit />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/utm-links" element={<UtmLinks />} />
            <Route path="/link" element={<Navigate to="/utm-links" replace />} />
            <Route path="/ig" element={<ShortRedirect to="/support?utm_source=instagram&utm_medium=social&utm_campaign=lancio&utm_content=bio" />} />
            <Route path="/igs" element={<ShortRedirect to="/support?utm_source=instagram&utm_medium=social&utm_campaign=lancio&utm_content=stories" />} />
            <Route path="/tt" element={<ShortRedirect to="/support?utm_source=tiktok&utm_medium=social&utm_campaign=lancio&utm_content=bio" />} />
            <Route path="/fb" element={<ShortRedirect to="/support?utm_source=facebook&utm_medium=social&utm_campaign=lancio" />} />
            <Route path="/wa" element={<ShortRedirect to="/support?utm_source=whatsapp&utm_medium=social&utm_campaign=lancio" />} />
            <Route path="/li" element={<ShortRedirect to="/support?utm_source=linkedin&utm_medium=social&utm_campaign=lancio" />} />
            <Route path="/yt" element={<ShortRedirect to="/support?utm_source=youtube&utm_medium=social&utm_campaign=lancio" />} />
            <Route path="/snapshot/:lang" element={<SnapshotEN />} />
            <Route path="/privacy" element={<Legal page="privacy" />} />
            <Route path="/cookie" element={<Legal page="cookie" />} />
            <Route path="/termini" element={<Legal page="terms" />} />
            <Route path="/terms" element={<Legal page="terms" />} />
            <Route path="/note-legali" element={<Legal page="legal" />} />
            <Route path="/legal-notice" element={<Legal page="legal" />} />
            <Route path="/saldo/:id" element={<BalancePage />} />
            <Route path="/balance/:id" element={<BalancePage />} />
            <Route path="/crociera/:id" element={<CuratedBooking />} />
            <Route path="/cruise/:id" element={<CuratedBooking />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </div>
  );
}

export default App;
